import { all, call, put, takeLatest } from 'redux-saga/effects';
import {
    joinProgramSuccess,
    joinProgramFail,
    createOffsiteCampaignSuccess,
    createOffsiteCampaignFail,
    requestToJoinCampaignSuccess,
    requestToJoinCampaignFail,
    approveCampaignJoinRequestSuccess,
    approveCampaignJoinRequestFail,
    rejectCampaignJoinRequestSuccess,
    rejectCampaignJoinRequestFail,
    getOffsiteCampaignDetailsSuccess,
    getOffsiteCampaignDetailsFail,
    getCampaignJobDetailsSuccess,
    getCampaignJobDetailsFail,
    manageOffsiteCampaignsSuccess,
    manageOffsiteCampaignsFail,
    updateOffsiteCampaignSuccess,
    updateOffsiteCampaignFail,
    deleteOffsiteCampaignSuccess,
    deleteOffsiteCampaignFail,
    getBrandsSuccess,
    getBrandsFail,
    addBrandSuccess,
    addBrandFail,
    updateBrandSuccess,
    updateBrandFail,
    deleteBrandSuccess,
    deleteBrandFail,
    requestDeliverableUpdateSuccess,
    requestDeliverableUpdateFail,
    sendCampaignMessageSuccess,
    sendCampaignMessageFail,
    deleteCampaignMessageSuccess,
    deleteCampaignMessageFail,
    updateCampaignMessageSuccess,
    updateCampaignMessageFail,
    approveDeliverableSubmissionSuccess,
    approveDeliverableSubmissionFail,
    schedulePostSuccess,
    schedulePostFail,
    uploadDeliverableSuccess,
    uploadDeliverableFail,
    getBuzzBridgeProfileSuccess,
    getBuzzBridgeProfileFail,
    updateBuzzBridgeProfileSuccess,
    updateBrand,
} from './actions';
import { IBuzzBridgeProfileResponse, ICreateBrand, IGetBrands, IGetBuzzBridgeProfile, types } from './types';
import { globalErrorHandler } from '../error/saga';
import { uploadFilesSaga } from '../upload/saga';
import { showToastMessage } from '../../utils/AppUtils';
import { Action } from 'redux';
import { brandUrl, influencersUrl } from '../../configurations/api/url';
import { api } from '../../configurations/api';
import { httpRequest } from '../types';
import { IGenericDocumentModifier } from '../../types/global/helper';

function isolateUploadFields(data: Record<string, any>) {
    // Keys that contain files and require upload handling
    const uploadKeys = ['documents', 'rawProfile'];

    // Separate uploads and non-upload body
    const uploads: Record<string, any> = {};
    const body: Record<string, any> = {};

    for (const key in data) {
        if (uploadKeys.includes(key)) {
            uploads[key] = data[key];
        } else {
            body[key] = data[key];
        }
    }

    return { uploads, body };
}

function* updateBuzzProfile({ payload }: { payload: IGenericDocumentModifier }): Generator<any, any, any> {
    const { body, uploads } = isolateUploadFields(payload.payload);
    const request = `${influencersUrl}/${payload.documentId}`;

    try {
        let profilePictureUrl: string | undefined;
        let updatedDocuments = [];

        /** Process uploads if they exist */
        if (uploads) {
            // Process rawProfile (e.g., if it's a file you want to upload)
            if ('rawProfile' in uploads && Array.isArray(uploads.rawProfile)) {
                if (uploads.rawProfile) {
                    profilePictureUrl = yield call(uploadFilesSaga, {
                        payload: {
                            id: payload.documentId,
                            files: uploads.rawProfile,
                            collection: 'influencer',
                            directory: 'media',
                            path: 'images',
                        },
                    }); // Your upload logic
                }
            }

            // Process each document if it has a file
            if ('documents' in uploads && Array.isArray(uploads.documents)) {
                updatedDocuments = yield all(
                    uploads.documents.map(function* (doc): any {
                        if (doc.file) {
                            const uploadedUrl = yield call(uploadFilesSaga, {
                                payload: {
                                    files: [doc.file],
                                    id: payload.documentId,
                                    collection: 'influencer',
                                    directory: 'media',
                                    path: 'documents',
                                },
                            });
                            const { file, ...rest } = doc;
                            return { ...rest, url: uploadedUrl[0] };
                        }
                        // Always remove 'file' key if it's present, even if undefined
                        const { file, ...rest } = doc;
                        return rest;
                    }),
                );
            }
        }

        /** Construct final payload */
        const finalPayload = {
            ...body,
            ...(profilePictureUrl && { profile_picture: profilePictureUrl[0] }),
            ...(updatedDocuments.length > 0 && { documents: updatedDocuments }),
        };

        const response = yield call(api, request, httpRequest.PATCH, finalPayload, 2, 1000);
        const updatedFields = Object.keys(payload.payload);
        yield put(updateBuzzBridgeProfileSuccess({ ...response.data.data, updatedFields }));
        yield call(showToastMessage, 'Profile updated successfully', 'success');
    } catch (error: any) {
        yield put(getBuzzBridgeProfileFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getBuzzProfile({
    payload,
    resolve,
    reject,
}: {
    payload: IGetBuzzBridgeProfile;
    resolve: (response: IBuzzBridgeProfileResponse) => Promise<void>;
    reject: (reason: any) => Promise<void>;
}): Generator<any, any, any> {
    const params = new URLSearchParams();
    if (payload.limit) params.append('limit', String(payload.limit));
    if (payload.page) params.append('page', String(payload.page));
    if (payload.group_type) params.append('group_type', String(payload.group_type));

    /** Url */
    const request = `${influencersUrl}/me/profile?${params.toString()}`; // GET
    try {
        const response: IBuzzBridgeProfileResponse = yield call(api, request, httpRequest.GET, null, 2, 1000);
        yield put(getBuzzBridgeProfileSuccess(response));
        if (resolve) resolve(response);
    } catch (error: any) {
        if (reject) reject(error);
        yield put(getBuzzBridgeProfileFail(error));
        yield call(globalErrorHandler, error);
    }
}

// Mock data for BuzzBridge
const mockBuzzBridgeData = {
    programStatus: 'active',
    brands: [
        {
            id: '1',
            name: 'FashionBrand',
            description: 'Premium fashion and lifestyle brand',
            logo: 'https://example.com/fashionbrand-logo.png',
            website: 'https://fashionbrand.com',
            industry: 'Fashion',
            contactEmail: 'partnerships@fashionbrand.com',
            contactPhone: '+1-555-0123',
            status: 'active',
            createdAt: '2024-01-15T10:00:00Z',
            updatedAt: '2024-06-01T14:30:00Z',
        },
        {
            id: '2',
            name: 'TechCorp',
            description: 'Innovative technology solutions provider',
            logo: 'https://example.com/techcorp-logo.png',
            website: 'https://techcorp.com',
            industry: 'Technology',
            contactEmail: 'marketing@techcorp.com',
            contactPhone: '+1-555-0456',
            status: 'active',
            createdAt: '2024-02-20T09:00:00Z',
            updatedAt: '2024-05-15T16:45:00Z',
        },
        {
            id: '3',
            name: 'BeautyGlow',
            description: 'Natural beauty and skincare products',
            logo: 'https://example.com/beautyglow-logo.png',
            website: 'https://beautyglow.com',
            industry: 'Beauty',
            contactEmail: 'collaborations@beautyglow.com',
            contactPhone: '+1-555-0789',
            status: 'pending',
            createdAt: '2024-03-10T11:00:00Z',
            updatedAt: '2024-03-10T11:00:00Z',
        },
    ],
    campaigns: [
        {
            id: '1',
            title: 'Summer Fashion Campaign',
            brand: 'FashionBrand',
            status: 'active',
            budget: 5000,
            influencers: 15,
            startDate: '2024-06-01',
            endDate: '2024-08-31',
        },
        {
            id: '2',
            title: 'Tech Product Launch',
            brand: 'TechCorp',
            status: 'pending',
            budget: 8000,
            influencers: 8,
            startDate: '2024-07-15',
            endDate: '2024-09-15',
        },
    ],
    campaignJobs: [
        {
            id: '1',
            campaignId: '1',
            title: 'Instagram Post Creation',
            description: 'Create engaging Instagram posts for summer fashion',
            requirements: ['High-quality images', 'Engaging captions', 'Hashtag strategy'],
            compensation: 500,
            deadline: '2024-06-15',
            status: 'open',
        },
        {
            id: '2',
            campaignId: '1',
            title: 'TikTok Video Production',
            description: 'Produce viral TikTok videos showcasing fashion trends',
            requirements: ['Creative video content', 'Trending music', 'Engaging transitions'],
            compensation: 800,
            deadline: '2024-06-20',
            status: 'in_progress',
        },
    ],
    managedCampaigns: [
        {
            id: '1',
            title: 'Summer Fashion Campaign',
            brand: 'FashionBrand',
            status: 'active',
            totalBudget: 5000,
            spentBudget: 3200,
            totalInfluencers: 15,
            activeInfluencers: 12,
            startDate: '2024-06-01',
            endDate: '2024-08-31',
            performance: {
                totalPosts: 45,
                totalEngagement: 125000,
                totalReach: 2500000,
                averageEngagementRate: 5.2,
            },
        },
        {
            id: '2',
            title: 'Tech Product Launch',
            brand: 'TechCorp',
            status: 'pending',
            totalBudget: 8000,
            spentBudget: 0,
            totalInfluencers: 8,
            activeInfluencers: 0,
            startDate: '2024-07-15',
            endDate: '2024-09-15',
            performance: {
                totalPosts: 0,
                totalEngagement: 0,
                totalReach: 0,
                averageEngagementRate: 0,
            },
        },
    ],
    messages: [
        {
            id: '1',
            campaignId: '1',
            sender: 'brand',
            content: 'Welcome to our summer campaign!',
            timestamp: '2024-06-01T10:00:00Z',
        },
        {
            id: '2',
            campaignId: '1',
            sender: 'influencer',
            content: 'Excited to be part of this campaign!',
            timestamp: '2024-06-01T11:00:00Z',
        },
    ],
    deliverables: [
        {
            id: '1',
            campaignId: '1',
            type: 'instagram_post',
            status: 'pending',
            dueDate: '2024-06-15',
            content: 'Summer fashion showcase',
        },
        {
            id: '2',
            campaignId: '1',
            type: 'tiktok_video',
            status: 'approved',
            dueDate: '2024-06-20',
            content: 'Fashion trend video',
        },
    ],
};

// Mock API delay
const mockApiCall = (data: any, delayMs: number = 1000) =>
    new Promise(resolve => setTimeout(() => resolve(data), delayMs));

// Saga functions
function* joinProgramSaga({ payload }: any): Generator<any, any, any> {
    try {
        // Mock API call
        const response = yield call(api, influencersUrl, httpRequest.POST, payload);
        const { data } = response.data;
        yield put(joinProgramSuccess(data));
        yield call(showToastMessage, 'Successfully requested to join BuzzBridge program!', 'success');
    } catch (error: any) {
        yield put(joinProgramFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* createOffsiteCampaignSaga({ payload }: any): Generator<any, any, any> {
    try {
        const newCampaign = {
            id: Date.now().toString(),
            ...payload,
            status: 'draft',
            createdAt: new Date().toISOString(),
        };
        const response = yield call(mockApiCall, newCampaign);
        yield put(createOffsiteCampaignSuccess(response));
        yield call(showToastMessage, 'Campaign created successfully!', 'success');
    } catch (error: any) {
        yield put(createOffsiteCampaignFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* requestToJoinCampaignSaga({ payload }: any): Generator<any, any, any> {
    try {
        const request = {
            id: Date.now().toString(),
            campaignId: payload.campaignId,
            influencerId: payload.influencerId,
            status: 'pending',
            message: payload.message,
            createdAt: new Date().toISOString(),
        };
        const response = yield call(mockApiCall, request);
        yield put(requestToJoinCampaignSuccess(response));
        yield call(showToastMessage, 'Request to join campaign sent!', 'success');
    } catch (error: any) {
        yield put(requestToJoinCampaignFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* approveCampaignJoinRequestSaga({ payload }: any): Generator<any, any, any> {
    try {
        const response = yield call(mockApiCall, {
            requestId: payload.requestId,
            status: 'approved',
        });
        yield put(approveCampaignJoinRequestSuccess(response));
        yield call(showToastMessage, 'Request approved successfully!', 'success');
    } catch (error: any) {
        yield put(approveCampaignJoinRequestFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* rejectCampaignJoinRequestSaga({ payload }: any): Generator<any, any, any> {
    try {
        const response = yield call(mockApiCall, {
            requestId: payload.requestId,
            status: 'rejected',
            reason: payload.reason,
        });
        yield put(rejectCampaignJoinRequestSuccess(response));
        yield call(showToastMessage, 'Request rejected', 'info');
    } catch (error: any) {
        yield put(rejectCampaignJoinRequestFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getOffsiteCampaignDetailsSaga({ payload }: any): Generator<any, any, any> {
    try {
        const campaign = mockBuzzBridgeData.campaigns.find(c => c.id === payload.campaignId);
        const response = yield call(mockApiCall, campaign);
        yield put(getOffsiteCampaignDetailsSuccess(response));
    } catch (error: any) {
        yield put(getOffsiteCampaignDetailsFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getCampaignJobDetailsSaga({ payload }: any): Generator<any, any, any> {
    try {
        const jobDetails = mockBuzzBridgeData.campaignJobs.filter(job => job.campaignId === payload.campaignId);
        const response = yield call(mockApiCall, jobDetails);
        yield put(getCampaignJobDetailsSuccess(response));
    } catch (error: any) {
        yield put(getCampaignJobDetailsFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* manageOffsiteCampaignsSaga({ payload }: any): Generator<any, any, any> {
    try {
        const managedCampaigns = mockBuzzBridgeData.managedCampaigns;
        const response = yield call(mockApiCall, managedCampaigns);
        yield put(manageOffsiteCampaignsSuccess(response));
    } catch (error: any) {
        yield put(manageOffsiteCampaignsFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* updateOffsiteCampaignSaga({ payload }: any): Generator<any, any, any> {
    try {
        const response = yield call(mockApiCall, {
            ...payload,
            updatedAt: new Date().toISOString(),
        });
        yield put(updateOffsiteCampaignSuccess(response));
        yield call(showToastMessage, 'Campaign updated successfully!', 'success');
    } catch (error: any) {
        yield put(updateOffsiteCampaignFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* deleteOffsiteCampaignSaga({ payload }: any): Generator<any, any, any> {
    try {
        const response = yield call(mockApiCall, {
            campaignId: payload.campaignId,
            deleted: true,
        });
        yield put(deleteOffsiteCampaignSuccess(response));
        yield call(showToastMessage, 'Campaign deleted successfully!', 'success');
    } catch (error: any) {
        yield put(deleteOffsiteCampaignFail(error));
        yield call(globalErrorHandler, error);
    }
}

// Brand management saga functions
function* getBrandsSaga({ payload }: { payload: IGetBrands }): Generator<any, any, any> {
    if (payload.searchTerm && payload.searchTerm.length > 0) {
        try {
            const response = yield call(api, `${brandUrl}/search`, httpRequest.POST, payload);
            const { data } = response;
            yield put(getBrandsSuccess(data));
        } catch (error: any) {
            yield put(getBrandsFail(error));
            yield call(globalErrorHandler, error);
        }
    } else {
        try {
            const response = yield call(api, brandUrl, httpRequest.GET, payload);
            const { data } = response.data;
            yield put(getBrandsSuccess(data));
        } catch (error: any) {
            yield put(getBrandsFail(error));
            yield call(globalErrorHandler, error);
        }
    }
}

function* addBrandSaga({ payload }: { payload: ICreateBrand }): Generator<any, any, any> {
    try {
        const response = yield call(api, brandUrl, httpRequest.POST, payload);
        const { data } = response.data;
        yield put(addBrandSuccess(data));
        if (payload.rawLogo) {
            yield put(updateBrand({ documentId: data._id, payload: { logo: payload.rawLogo }, fromCreate: true }));
        }
    } catch (error: any) {
        yield put(addBrandFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* updateBrandSaga({ payload }: { payload: IGenericDocumentModifier }): Generator<any, any, any> {
    const body = { ...payload.payload };
    try {
        if (payload.fromCreate) {
            // Upload
            const imageUrl = yield call(uploadFilesSaga, {
                payload: {
                    collection: 'brands',
                    directory: 'assets',
                    files: payload.payload.logo,
                    id: payload.documentId,
                    path: 'images',
                },
            });

            if (imageUrl) {
                body.logo = imageUrl[0];
            }
        }
        const response = yield call(api, `${brandUrl}/${payload.documentId}`, httpRequest.PATCH, body);
        const { data } = response.data;
        yield put(updateBrandSuccess(data));
        if (payload.fromCreate) {
            yield call(showToastMessage, 'Brand added successfully!', 'success');
        } else {
            yield call(showToastMessage, 'Brand updated successfully!', 'success');
        }
    } catch (error: any) {
        yield put(updateBrandFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* deleteBrandSaga({ payload }: { payload: IGenericDocumentModifier }): Generator<any, any, any> {
    try {
        yield call(api, `${brandUrl}/${payload.documentId}`, httpRequest.DELETE, {});
        yield put(deleteBrandSuccess(payload));
        yield call(showToastMessage, 'Brand deleted successfully!', 'success');
    } catch (error: any) {
        yield put(deleteBrandFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* requestDeliverableUpdateSaga({ payload }: any): Generator<any, any, any> {
    try {
        const request = {
            id: Date.now().toString(),
            deliverableId: payload.deliverableId,
            reason: payload.reason,
            status: 'pending',
            createdAt: new Date().toISOString(),
        };
        const response = yield call(mockApiCall, request);
        yield put(requestDeliverableUpdateSuccess(response));
        yield call(showToastMessage, 'Update request sent!', 'success');
    } catch (error: any) {
        yield put(requestDeliverableUpdateFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* sendCampaignMessageSaga({ payload }: any): Generator<any, any, any> {
    try {
        const message = {
            id: Date.now().toString(),
            campaignId: payload.campaignId,
            sender: payload.sender,
            content: payload.content,
            timestamp: new Date().toISOString(),
        };
        const response = yield call(mockApiCall, message);
        yield put(sendCampaignMessageSuccess(response));
    } catch (error: any) {
        yield put(sendCampaignMessageFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* deleteCampaignMessageSaga({ payload }: any): Generator<any, any, any> {
    try {
        const response = yield call(mockApiCall, {
            messageId: payload.messageId,
            deleted: true,
        });
        yield put(deleteCampaignMessageSuccess(response));
        yield call(showToastMessage, 'Message deleted!', 'success');
    } catch (error: any) {
        yield put(deleteCampaignMessageFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* updateCampaignMessageSaga({ payload }: any): Generator<any, any, any> {
    try {
        const response = yield call(mockApiCall, {
            ...payload,
            updatedAt: new Date().toISOString(),
        });
        yield put(updateCampaignMessageSuccess(response));
        yield call(showToastMessage, 'Message updated!', 'success');
    } catch (error: any) {
        yield put(updateCampaignMessageFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* approveDeliverableSubmissionSaga({ payload }: any): Generator<any, any, any> {
    try {
        const response = yield call(mockApiCall, {
            deliverableId: payload.deliverableId,
            status: 'approved',
            feedback: payload.feedback,
        });
        yield put(approveDeliverableSubmissionSuccess(response));
        yield call(showToastMessage, 'Deliverable approved!', 'success');
    } catch (error: any) {
        yield put(approveDeliverableSubmissionFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* schedulePostSaga({ payload }: any): Generator<any, any, any> {
    try {
        const scheduledPost = {
            id: Date.now().toString(),
            campaignId: payload.campaignId,
            platform: payload.platform,
            content: payload.content,
            scheduledDate: payload.scheduledDate,
            status: 'scheduled',
        };
        const response = yield call(mockApiCall, scheduledPost);
        yield put(schedulePostSuccess(response));
        yield call(showToastMessage, 'Post scheduled successfully!', 'success');
    } catch (error: any) {
        yield put(schedulePostFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* uploadDeliverableSaga({ payload }: any): Generator<any, any, any> {
    try {
        const deliverable = {
            id: Date.now().toString(),
            campaignId: payload.campaignId,
            type: payload.type,
            content: payload.content,
            files: payload.files,
            status: 'submitted',
            submittedAt: new Date().toISOString(),
        };
        const response = yield call(mockApiCall, deliverable);
        yield put(uploadDeliverableSuccess(response));
        yield call(showToastMessage, 'Deliverable uploaded successfully!', 'success');
    } catch (error: any) {
        yield put(uploadDeliverableFail(error));
        yield call(globalErrorHandler, error);
    }
}

// Action interfaces for TypeScript
interface JoinProgramAction extends Action {
    payload: any;
}

interface CreateCampaignAction extends Action {
    payload: any;
}

interface RequestJoinAction extends Action {
    payload: any;
}

interface ApproveRequestAction extends Action {
    payload: any;
}

interface RejectRequestAction extends Action {
    payload: any;
}

interface GetCampaignDetailsAction extends Action {
    payload: any;
}

interface GetCampaignJobDetailsAction extends Action {
    payload: any;
}

interface ManageOffsiteCampaignsAction extends Action {
    payload: any;
}

interface UpdateCampaignAction extends Action {
    payload: any;
}

interface DeleteCampaignAction extends Action {
    payload: any;
}

interface GetBrandsAction extends Action {
    payload: any;
}

interface AddBrandAction extends Action {
    payload: any;
}

interface UpdateBrandAction extends Action {
    payload: any;
}

interface DeleteBrandAction extends Action {
    payload: any;
}

interface RequestUpdateAction extends Action {
    payload: any;
}

interface SendMessageAction extends Action {
    payload: any;
}

interface DeleteMessageAction extends Action {
    payload: any;
}

interface UpdateMessageAction extends Action {
    payload: any;
}

interface ApproveDeliverableAction extends Action {
    payload: any;
}

interface SchedulePostAction extends Action {
    payload: any;
}

interface UploadDeliverableAction extends Action {
    payload: any;
}

interface GetBuzzProfileAction extends Action {
    payload: IGetBuzzBridgeProfile;
    resolve: (response: IBuzzBridgeProfileResponse) => Promise<void>;
    reject: (reason: any) => Promise<void>;
}

interface updateBuzzProfileAction extends Action {
    payload: IGenericDocumentModifier;
}

// Root saga
export default function* buzzbridgeSaga(): Generator<any, any, any> {
    yield all([
        takeLatest<JoinProgramAction>(types.JOIN_PROGRAM, joinProgramSaga),
        takeLatest<CreateCampaignAction>(types.CREATE_OFFSITE_CAMPAIGN, createOffsiteCampaignSaga),
        takeLatest<RequestJoinAction>(types.REQUEST_TO_JOIN_CAMPAIGN, requestToJoinCampaignSaga),
        takeLatest<ApproveRequestAction>(types.APPROVE_CAMPAIGN_JOIN_REQUEST, approveCampaignJoinRequestSaga),
        takeLatest<RejectRequestAction>(types.REJECT_CAMPAIGN_JOIN_REQUEST, rejectCampaignJoinRequestSaga),
        takeLatest<GetCampaignDetailsAction>(types.GET_OFFSITE_CAMPAIGN_DETAILS, getOffsiteCampaignDetailsSaga),
        takeLatest<GetCampaignJobDetailsAction>(types.GET_CAMPAIGN_JOB_DETAILS, getCampaignJobDetailsSaga),
        takeLatest<ManageOffsiteCampaignsAction>(types.MANAGE_OFFSITE_CAMPAIGNS, manageOffsiteCampaignsSaga),
        takeLatest<UpdateCampaignAction>(types.UPDATE_OFFSITE_CAMPAIGN, updateOffsiteCampaignSaga),
        takeLatest<DeleteCampaignAction>(types.DELETE_OFFSITE_CAMPAIGN, deleteOffsiteCampaignSaga),
        takeLatest<GetBrandsAction>(types.GET_BRANDS, getBrandsSaga),
        takeLatest<AddBrandAction>(types.ADD_BRAND, addBrandSaga),
        takeLatest<UpdateBrandAction>(types.UPDATE_BRAND, updateBrandSaga),
        takeLatest<DeleteBrandAction>(types.DELETE_BRAND, deleteBrandSaga),
        takeLatest<RequestUpdateAction>(types.REQUEST_DELIEVERABLE_UPDATE, requestDeliverableUpdateSaga),
        takeLatest<SendMessageAction>(types.SEND_CAMPAIGN_MESSAGE, sendCampaignMessageSaga),
        takeLatest<DeleteMessageAction>(types.DELETE_CAMPAIGN_MESSAGE, deleteCampaignMessageSaga),
        takeLatest<UpdateMessageAction>(types.UPDATE_CAMPAIGN_MESSAGE, updateCampaignMessageSaga),
        takeLatest<ApproveDeliverableAction>(types.APPROVE_DELIVERABLE_SUBMISSION, approveDeliverableSubmissionSaga),
        takeLatest<SchedulePostAction>(types.SCHEDULE_POST, schedulePostSaga),
        takeLatest<UploadDeliverableAction>(types.UPLOAD_DELIVERABLE, uploadDeliverableSaga),
        takeLatest<GetBuzzProfileAction>(types.GET_BUZZBRIDGE_PROFILE, getBuzzProfile),
        takeLatest<updateBuzzProfileAction>(types.UPDATE_BUZZBRIDGE_PROFILE, updateBuzzProfile),
    ]);
}
